import { saveOutlookRequestToken as saveOrgOutlookRequestToken } from "../../../my-organization/OrganizationEmail/organizationEmailApi";
import { saveOutlookRequestToken as savePersonalOutlookRequestToken } from "../personalEmailApi";
import { ORG_OUTLOOK_OAUTH_STATE } from "../../../../utils/microsoftOAuth";
import { PubSub, TopicsEnum, NotificationType } from "../../../../utils/pubsub";

/**
 * Pure dispatch logic for the shared /callback/outlook OAuth return route — extracted out of the
 * component (mirrors gmailAuthReturnHandler.ts) so it's unit-testable without a DOM. Both the personal
 * (Messaging > Email) and organization (My Organization > Email) Outlook connections land here, since
 * they share the one redirect_uri Azure AD has registered for this app's OAuth client; `state` (echoed
 * back unchanged by Microsoft) says which flow this callback belongs to. Same generic
 * "no code -> fallback route, log error" handling as the existing Gmail callback handler — no dedicated
 * access_denied/consent_required UX in this phase.
 */
export const handleOutlookOAuthReturn = async (
  code: string | null,
  state: string | null,
  redirectUri: string
): Promise<string> => {
  const isOrgFlow = state === ORG_OUTLOOK_OAUTH_STATE;
  const fallbackRoute = isOrgFlow ? "/organization/email" : "/messaging/email";

  if (!code) {
    console.error("Code parameter not found in URL");
    return fallbackRoute;
  }

  try {
    if (isOrgFlow) {
      await saveOrgOutlookRequestToken(code, redirectUri);
    } else {
      await savePersonalOutlookRequestToken(code, redirectUri);
    }
  } catch (error) {
    console.error("Error making API call:", error);
    // Both save*OutlookRequestToken helpers now throw the non-ok response on failure (e.g. a
    // PermissionDeniedException for a non-org-user/non-Admin caller, or a Microsoft token-exchange
    // failure) -- surface it here instead of navigating back with no feedback, since the connect
    // screen itself can't distinguish "never connected" from "just failed to connect".
    PubSub.publish(TopicsEnum.Notification.toString(), {
      type: NotificationType.Failure,
      title: "Failure",
      message: "Unable to connect the Outlook account. Please try again.",
    });
  }

  return fallbackRoute;
};
